import { unitsToTile } from '../core/fixed';
import type { Unit } from '../entities/unit';

/**
 * Which units are on which tile (CLAUDE.md §4.6: proximity by grid, never by scanning).
 * Rebuilt at the start of every tick from the unit list in id order, and updated when a
 * unit spawns mid-tick.
 *
 * - `standingAt`: the lowest-id unit that is standing still (no path, not hidden). Standing
 *   units are obstacles for pathfinding; walking units are not.
 * - `countAt` / `visibleCountAt`: how many units (any state) are on the tile.
 */
export class UnitGrid {
  private readonly width: number;
  private readonly height: number;
  private readonly standing: Int32Array;
  private readonly count: Uint16Array;
  private readonly visible: Uint16Array;
  /** Unit ids per tile in id order, for range queries. */
  private readonly buckets = new Map<number, number[]>();

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
    this.standing = new Int32Array(width * height);
    this.count = new Uint16Array(width * height);
    this.visible = new Uint16Array(width * height);
  }

  rebuild(unitsInIdOrder: Iterable<Unit>): void {
    this.standing.fill(0);
    this.count.fill(0);
    this.visible.fill(0);
    this.buckets.clear();
    for (const u of unitsInIdOrder) this.add(u);
  }

  add(u: Unit): void {
    const i = unitsToTile(u.y) * this.width + unitsToTile(u.x);
    if (i < 0 || i >= this.count.length) return;
    this.count[i] = (this.count[i] ?? 0) + 1;
    const bucket = this.buckets.get(i);
    if (bucket) bucket.push(u.id);
    else this.buckets.set(i, [u.id]);
    if (!u.hidden && u.state.kind !== 'dying') {
      this.visible[i] = (this.visible[i] ?? 0) + 1;
      if (u.path === null && this.standing[i] === 0) this.standing[i] = u.id;
    }
  }

  /** Ids of units within Chebyshev distance `r` of (cx, cy): tiles in fixed scan order, ids in id order. */
  unitsWithin(cx: number, cy: number, r: number): number[] {
    const out: number[] = [];
    const y0 = Math.max(0, cy - r);
    const y1 = Math.min(this.height - 1, cy + r);
    const x0 = Math.max(0, cx - r);
    const x1 = Math.min(this.width - 1, cx + r);
    for (let ty = y0; ty <= y1; ty++) {
      for (let tx = x0; tx <= x1; tx++) {
        const b = this.buckets.get(ty * this.width + tx);
        if (b) for (const id of b) out.push(id);
      }
    }
    return out;
  }

  /** Id of the standing unit on the tile, or 0. */
  standingAt(tx: number, ty: number): number {
    return this.standing[ty * this.width + tx] ?? 0;
  }

  countAt(tx: number, ty: number): number {
    return this.count[ty * this.width + tx] ?? 0;
  }

  visibleCountAt(tx: number, ty: number): number {
    return this.visible[ty * this.width + tx] ?? 0;
  }
}

/** Extra obstacles for pathfinding beyond the map itself. */
export type OccupancyFn = (tx: number, ty: number) => boolean;
